<script setup lang="ts">
  import AlertsSheet from '@/components/alerts/AlertsSheet.vue'
  import WhiteLogo from '@/components/Logo.vue'
  import BellIcon from '@/icons/bell-icon.vue'
  import type { MenuItem } from '@/routes'
  import { useAlertStore } from '@/store/alert.store'
  import { usePortalCustomizationStore } from '@/store/portal-customization.store'
  import type { NestUser } from '@/types/user'
  import { isLightColor, resolvePortalColors } from '@/utils/portal-theme'
  import { Account, Logout, Logo, MenuBar } from '@logistiek-zonder-papier/lzp-library'
  import { computed, onMounted, ref } from 'vue'
  import { useRouter } from 'vue-router'

  const router = useRouter()
  const alertStore = useAlertStore()
  const portalCustomizationStore = usePortalCustomizationStore()

  const { user } = defineProps<{
    user: NestUser
    menu: MenuItem[]
  }>()

  const showAlerts = ref<boolean>(false)

  const useWhiteLogo = computed<boolean>(() =>
    isLightColor(
      resolvePortalColors(portalCustomizationStore.activeColors).secondaryForegroundColor
    )
  )

  onMounted(async () => {
    await alertStore.fetchUnreadAlertCount()
  })

  function logout() {
    localStorage.removeItem('jwt_token')
    router.replace('/login')
  }
</script>

<template>
  <div class="sidebar">
    <div class="logo-container">
      <img
        v-if="portalCustomizationStore.logoUrl"
        class="logo custom-logo"
        :src="portalCustomizationStore.logoUrl"
        alt="Logo"
      />
      <WhiteLogo v-else-if="useWhiteLogo" class="logo" />
      <Logo v-else class="logo" />
    </div>

    <MenuBar class="menu-bar" :menu-config="menu" />

    <div class="account-container">
      <div class="account">
        <Account />
        <div>{{ user.firstName }} {{ user.lastName }}</div>
      </div>

      <button type="button" class="sidebar-action" @click="showAlerts = true">
        <BellIcon class="sidebar-action-icon" />
        <div>Meldingen</div>
        <span v-if="alertStore.unreadAlertCount > 0" class="unread-count">
          {{ alertStore.unreadAlertCount }}
        </span>
      </button>

      <button type="button" class="sidebar-action" @click="logout()">
        <Logout class="sidebar-action-icon" />
        <div>Uitloggen</div>
      </button>
    </div>

    <AlertsSheet v-model:show="showAlerts" />
  </div>
</template>

<style lang="css" scoped>
  .sidebar {
    display: flex;
    flex-direction: column;
    justify-content: start;
    width: var(--spacing-xxl);
    min-height: 100%;
    padding-bottom: var(--spacing-s);
    --sidebar-text: var(--colours-sidebar-text, var(--colours-grey-5));
    --sidebar-divider: color-mix(in srgb, var(--sidebar-text) 15%, transparent);
    background-color: var(--colours-sidebar, transparent);
    color: var(--sidebar-text);
  }

  .menu-bar :deep(.menu-item) {
    color: var(--sidebar-text);
  }

  .menu-bar :deep(.menu-item:not(.active-menu-item):hover),
  .sidebar-action:hover {
    background-color: color-mix(in srgb, var(--sidebar-text) 8%, transparent);
  }

  .menu-bar {
    padding: var(--spacing-s);
    overflow-y: auto;
  }

  .account-container {
    user-select: none;
    border-top: 1px solid var(--sidebar-divider);
    padding: var(--spacing-s);
    display: flex;
    flex-direction: column;
  }

  .account {
    border-radius: var(--components-component-corners);
    padding: var(--spacing-xs) var(--spacing-s);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-xs);
  }

  .sidebar-action:hover {
    cursor: pointer;
  }

  .sidebar-action {
    border: none;
    background: none;
    color: inherit;
    font: inherit;
    text-align: left;
    border-radius: var(--components-component-corners);
    padding: var(--spacing-xs) var(--spacing-s);
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: var(--spacing-xs);
  }

  .sidebar-action-icon {
    width: 20px;
    height: 20px;
  }

  .unread-count {
    margin-left: auto;
    min-width: 1.25rem;
    padding: 0 0.375rem;
    border-radius: 0.625rem;
    background-color: var(--colours-brand-colour-1);
    color: var(--colours-primary-text);
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1.25rem;
    text-align: center;
  }

  .menu-bar :deep(.active-menu-item > *) {
    color: var(--colours-primary-text);
  }

  .logo-container {
    display: flex;
    min-height: var(--top-bar-height);
    border-bottom: 1px solid var(--sidebar-divider);
    justify-content: center;
    align-items: center;
    padding: var(--spacing-xs) var(--spacing-s);
  }

  .logo {
    height: var(--components-component-height);
  }

  .custom-logo {
    height: auto;
    max-height: calc(var(--top-bar-height) - 2 * var(--spacing-xs));
    max-width: 100%;
    object-fit: contain;
  }
</style>
